import useJwt from '@/auth/jwt/useJwt'
import store from '@/store'
import { useCognito } from '@/views/habit/awsCognito'
import { useLambda } from '@/views/habit/awsLambda'

const { isAuthenticated } = useCognito()

/**
 * Return if user is logged in
 * This is completely up to you and how you want to store the token in your frontend application
 * e.g. If you are using cookies to store the application please update this function
 */
// eslint-disable-next-line arrow-body-style
export const isUserLoggedIn = () => {
  return localStorage.getItem('userData') && isAuthenticated()
}

export const getUserData = () => store.state?.userStore?.userData

/**
 * Fusiona permission_default[role] con worker_permission_override[worker_id] (si existe),
 * acción por acción — donde el override tiene esa acción explícita gana el override, si no
 * queda el default. Compartido entre Login.vue (primer login) y syncUserPermissions (F5).
 */
export const mergePermissions = (defaultModules = {}, overrideModules = {}) => {
  const moduleKeys = new Set([...Object.keys(defaultModules), ...Object.keys(overrideModules)])
  const merged = {}

  moduleKeys.forEach(moduleKey => {
    merged[moduleKey] = { ...defaultModules[moduleKey], ...overrideModules[moduleKey] }
  })

  return merged
}

/**
 * Re-fetches permission_default[role] + worker_permission_override[worker_id] y refresca
 * userData.permissions (store + localStorage) con el resultado fusionado.
 * Debe correr ANTES de montar la app (ver main.js) — App.vue elige el layout (y con él,
 * VerticalNavMenu) leyendo el $route inicial "START" de Vue Router, que existe antes de que
 * el primer beforeEach termine. VerticalNavMenu lee userData una sola vez al montarse, no
 * reactivo: si esto corriera en el guard del router (como se probó primero), el sidebar ya
 * habría montado con el snapshot viejo de localStorage antes de que el fetch resuelva —
 * el fix recién se reflejaba una recarga después de la que lo disparaba.
 */
export const syncUserPermissions = async () => {
  const userData = getUserData()
  if (!userData?.role) return

  try {
    const { getItem } = useLambda()
    const [permissionDoc, overrideDoc] = await Promise.all([
      getItem({ collection: 'permission_default', query: { privilege: userData.role } }),
      userData.worker_id
        ? getItem({ collection: 'worker_permission_override', query: { worker_id: userData.worker_id } })
        : Promise.resolve(null),
    ])
    const updated = { ...userData, permissions: mergePermissions(permissionDoc?.modules, overrideDoc?.modules) }
    localStorage.setItem('userData', JSON.stringify(updated))
    store.commit('userStore/updateUserData', updated)
  } catch (error) {
    console.log(error)
  }
}

/**
 * This function is used for demo purpose route navigation
 * In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 * Please note role field is just for showing purpose it's not used by anything in frontend
 * We are checking role just for ease
 * NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = userRole => {
  if (userRole === 'admin') return '/'
  if (userRole === 'client') return { name: 'access-control' }
  return { name: 'auth-login' }
}

// Home candidates that carry a real Capa 1 permission gate (module/action, ver
// router/routes/*.js) — necesario acá porque este helper no puede importar `router` sin
// crear un ciclo real (router/index.js ya importa desde este mismo archivo).
const HOME_ROUTE_PERMISSION = {
  'habit-general-dashboard': { module: 'general-dashboard', action: 'view' },
  'organization-complaint-list': { module: 'complaint-channel', action: 'view' },
}

/**
 * Resuelve a dónde mandar a un usuario logueado que "quiere ir a home" (login con sesión
 * activa, botón "Back to Home" de NotAuthorized/InactiveAccount) — a diferencia de
 * `getHomeRouteForLoggedInUser` (código muerto del template, solo conoce roles `admin`/
 * `client` que no existen acá), esta función SÍ valida `userData.permissions` antes de
 * elegir un candidato, para no volver a mandar a un usuario a una ruta a la que no tiene
 * acceso — eso generaba un rebote perpetuo hacia `misc-not-authorized` (ver permisos-plan.md).
 *
 * Regla de negocio (confirmada por la usuaria): `employee` entra a `organization-complaint-
 * list`; `supervisor`/`manager`/`consultant` entran a `habit-general-dashboard`. Si el
 * consultor le saca la vista de ESE módulo a un privilegio (caso real: por ejemplo, un
 * `employee` sin `complaint-channel.view`), se prueba con el otro dashboard conocido antes
 * de rendirse — así alguien con vista habilitada en cualquiera de los dos igual entra a algo
 * útil, no directo a "no autorizado".
 *
 * Si NINGÚN candidato aplica (worker sin ningún módulo de "home" habilitado, caso real y
 * válido — no un error), el fallback final es quedarse en `misc-not-authorized`, NO
 * `apps-pc-team`: ese dashboard es de supervisión (`workersSupervisedWithEvents`, pensado
 * para roles con gente a cargo), no una landing neutral — mandarlo ahí a un `employee` sin
 * reportes a cargo no tiene sentido y además falla. `auth-login` tampoco sirve de fallback
 * acá porque tiene `meta.redirectIfLoggedIn`, solo volvería a llamar a esta misma función
 * — un loop. `misc-not-authorized` no tiene ese meta, no hace fetch de datos, y es honesto:
 * ese worker realmente no tiene ningún módulo de home asignado, hay que decírselo, no
 * mandarlo a una pantalla al azar.
 */
export const resolveHomeRoute = () => {
  const userData = getUserData()
  if (!userData?.role) return { name: 'auth-login' }

  const clientFunctionality = JSON.parse(localStorage.getItem('clientData') || '{}')

  const primaryRole = userData.role === 'employee' ? 'organization-complaint-list' : 'habit-general-dashboard'
  const secondaryRole = primaryRole === 'organization-complaint-list' ? 'habit-general-dashboard' : 'organization-complaint-list'

  const candidates = []
  if (clientFunctionality.default_view_pc_team) candidates.push('apps-pc-team')
  if (clientFunctionality.default_view_daily_dialogue) candidates.push('habit-meeting-new')
  candidates.push(primaryRole, secondaryRole)

  const allowedName = candidates.find(name => {
    const gate = HOME_ROUTE_PERMISSION[name]
    return !gate || userData.permissions?.[gate.module]?.[gate.action]?.allowed
  })

  return { name: allowedName || 'misc-not-authorized' }
}
